import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { ArrowLeft, Mail, User, Calendar, Eye, MousePointerClick } from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import { formatDateTime } from "../../utils/dateUtils";

import {
  PageHeader,
  Card,
  Button,
  Badge,
  Spinner,
  EmptyState,
  DescriptionList,
  Table,
} from "../../design-system";

const SEND_STATUS = {
  SENT:    { variant: "success", label: "Envoyé" },
  FAILED:  { variant: "danger",  label: "Échec" },
  PENDING: { variant: "neutral", label: "En attente" },
};

class NewsletterDetail extends Component {
  constructor(props) {
    super(props);
    this.state = { subscriber: null, loading: true };
  }

  componentDidMount() {
    this.loadSubscriber();
  }

  loadSubscriber = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const { id } = this.props.match.params;
      const response = await axios.get(`/api/newsletters/subscribers/${id}`);
      this.setState({ subscriber: response.data.subscriber, loading: false });
    } catch (error) {
      console.error("Error loading subscriber:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible de charger l'abonné",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      if (error.response?.status === 404) {
        setTimeout(() => this.props.history.push("/dashboard/newsletter"), 2000);
      }
    }
  };

  render() {
    const { subscriber, loading } = this.state;

    if (loading) {
      return (
        <AdminPageShell pageTitle="Détail abonné">
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    if (!subscriber) {
      return (
        <AdminPageShell pageTitle="Détail abonné">
          <EmptyState
            title="Abonné non trouvé"
            description="L'abonné demandé n'existe pas."
            action={<Button as={Link} to="/dashboard/newsletter">Retour à la liste</Button>}
          />
        </AdminPageShell>
      );
    }

    const sents = subscriber.newsletterSents || [];

    return (
      <AdminPageShell pageTitle="Détail abonné" breadcrumbLast={subscriber.name || subscriber.email}>
        <PageHeader
          eyebrow="— Newsletter"
          title={subscriber.name || subscriber.email}
          description={subscriber.name ? subscriber.email : "Détail de l'abonné"}
          badge={subscriber.active
            ? <Badge variant="success" dot>Actif</Badge>
            : <Badge variant="neutral" dot>Désabonné</Badge>}
          actions={
            <Button
              as={Link}
              to="/dashboard/newsletter"
              variant="secondary"
              leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
            >
              Retour à la liste
            </Button>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="space-y-5">
            <Card>
              <Card.Header><Card.Title>Informations</Card.Title></Card.Header>
              <DescriptionList orientation="vertical" items={[
                {
                  term: "Email",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <a href={`mailto:${subscriber.email}`} className="underline-offset-2 hover:underline">{subscriber.email}</a>
                    </span>
                  ),
                },
                ...(subscriber.name ? [{
                  term: "Nom",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      {subscriber.name}
                    </span>
                  ),
                }] : []),
                {
                  term: "Abonné le",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <span className="font-mono text-xs">{formatDateTime(subscriber.subscribedAt)}</span>
                    </span>
                  ),
                },
                ...(subscriber.unsubscribedAt ? [{
                  term: "Désabonné le",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <span className="font-mono text-xs">{formatDateTime(subscriber.unsubscribedAt)}</span>
                    </span>
                  ),
                }] : []),
              ]} />
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-5">
            <Card padding="none" className="overflow-hidden">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <h3 className="text-sm font-bold tracking-tight">Historique des newsletters</h3>
                <span className="text-xs text-mute font-mono">{sents.length} envoi(s)</span>
              </div>
              {sents.length === 0 ? (
                <div className="p-6">
                  <p className="text-sm text-mute text-center">Aucune newsletter envoyée à cet abonné.</p>
                </div>
              ) : (
                <Table>
                  <Table.Head>
                    <Table.Row>
                      <Table.Th>Article</Table.Th>
                      <Table.Th className="w-36">Envoyé le</Table.Th>
                      <Table.Th className="w-24">Statut</Table.Th>
                      <Table.Th className="w-32">Ouvert</Table.Th>
                      <Table.Th className="w-32">Cliqué</Table.Th>
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {sents.map((sent) => {
                      const status = SEND_STATUS[sent.status] || SEND_STATUS.PENDING;
                      return (
                        <Table.Row key={sent.id}>
                          <Table.Cell>
                            {sent.article ? (
                              <Link
                                to={`/dashboard/articles/${sent.article.id}/view`}
                                className="font-semibold text-text hover:underline underline-offset-2"
                              >
                                {sent.article.title}
                              </Link>
                            ) : (
                              <span className="text-mute italic">Article supprimé</span>
                            )}
                          </Table.Cell>
                          <Table.Cell className="font-mono text-xs text-mute">{formatDateTime(sent.sentAt)}</Table.Cell>
                          <Table.Cell><Badge variant={status.variant} dot>{status.label}</Badge></Table.Cell>
                          <Table.Cell>
                            {sent.openedAt
                              ? <Badge variant="success" leadingIcon={<Eye className="w-3 h-3" strokeWidth={2} />}>
                                  <span className="font-mono text-[10px]">{formatDateTime(sent.openedAt)}</span>
                                </Badge>
                              : <span className="text-xs text-mute">Non ouvert</span>}
                          </Table.Cell>
                          <Table.Cell>
                            {sent.clickedAt
                              ? <Badge variant="success" leadingIcon={<MousePointerClick className="w-3 h-3" strokeWidth={2} />}>
                                  <span className="font-mono text-[10px]">{formatDateTime(sent.clickedAt)}</span>
                                </Badge>
                              : <span className="text-xs text-mute">Non cliqué</span>}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              )}
            </Card>
          </div>
        </div>
      </AdminPageShell>
    );
  }
}

export default withRouter(NewsletterDetail);
